import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3, Briefcase, Check, CheckCircle2, History, ChevronLeft, ChevronRight, ClipboardCheck, Clock, Coffee, Info, Lock,
  LucideIcon, Mail, PartyPopper, Play, RefreshCw, Send, Square, Stethoscope, Sun, UserX, WifiOff,
} from 'lucide-react';
import {
  describePortalError,
  getPortalActivity,
  getPortalHolidays,
  getPortalMealWaiverDates,
  getPortalPayPeriodsAround,
  getPortalTimeSummary,
  getPortalTimesheet,
  getPortalTimesheetQuestions,
  getPortalTimesheetStatus,
} from '../../../api/portal';
import type { PortalErrorInfo } from './portalErrors';
import { usePortalData } from './PortalContext';
import { jobCode, namedNonJob, selectStrip, shortRange, workStateOf } from './portalModel';
import { addDaysYmd, fmtDayShort, fmtHours, fmtYmdDow, fmtYmdShort, localYmd } from './portalTime';
import {
  adjacentPeriod,
  dayJobs,
  dayMinutes,
  dayPunches,
  DayTotals,
  DOW_LONG,
  DOW_SHORT,
  dowIndex,
  mergePeriods,
  periodContaining,
  railSteps,
  railView,
  rowsOnDay,
  shortTime,
  submitBlockReason,
  sumTotals,
  totalsByDay,
  toTsRows,
  weekLabel,
  weekRowIDs,
  weekYmds,
  ZERO_TOTALS,
} from './timesheetModel';
import { questionText, submittedAnswers, visibleQuestions } from './submitModel';
import DayDetailModal from './DayDetailModal';
import SubmitWizard, { WizardDay } from './SubmitWizard';
import { usePortalToast } from './usePortalToast';
import ActivityRow from './ActivityRow';
import { itemsForWeek } from './activityModel';
import { useSiteContext } from '../../../SiteContext';
import { Page } from '../../../interfaces/PagesInterface';
import './myActivity.css';
import {
  ANSWER_MEAL_WAIVER,
  PortalActivityItem,
  PortalHoliday,
  PortalMealWaiverDates,
  PortalPayPeriod,
  PortalTimeSummary,
  PortalTimesheetQuestion,
  PortalTimesheetResponse,
  PortalTimesheetStatus,
} from './portalTypes';
import { portalJobMode, portalSubmitBlockedByRole } from './portalRoleRules';
import './employeePortal.css';
import './myTimesheet.css';

/**
 * Employee Time Portal — My Timesheet (P-TAS-20 Phase 3, App-2; handoff §3–6, screenshots
 * 15–33). One Mon–Sun week per pay period:
 *   - week stepper (/portal/payPeriods/getAroundDate), status rail (/portal/timesheet/status:
 *     Submitted → Supervisor approved → Payroll approved → Exported), week note, the week
 *     grid (a day list below 820px of content), totals from getTimeSummary (every row ID
 *     sent), overtime and questions cards;
 *   - day detail: edit / add missing / delete punches while the week is open;
 *   - submit wizard: review → questions → type-to-sign → POST /portal/timesheet.
 * No submit deadline and no "late" (Steven's override of handoff D2): submit any time.
 * After submit the week is locked until payroll or a supervisor unlocks it; the page says so.
 * Fixed-height regions per D7 (strip 60, rail 80, week note 50, cards 340).
 */

interface WeekData {
  payPeriodID: number;
  timesheet: PortalTimesheetResponse | null;
  status: PortalTimesheetStatus | null;
  holidays: PortalHoliday[];
  waiver: PortalMealWaiverDates | null;
  waiverFailed: boolean;
  summary: PortalTimeSummary | null;
  summaryError: string | null;
  /** This week's events from GET /portal/activity ("Changes to this week"); null when it failed. */
  activity: PortalActivityItem[] | null;
  error: PortalErrorInfo | null;
}

type Settled<T> = { ok: true; v: T } | { ok: false; info: PortalErrorInfo };

const CATEGORIES: { key: keyof DayTotals; label: string; color: string; caOnly?: boolean }[] = [
  { key: 'reg', label: 'Regular', color: 'var(--ok)' },
  { key: 'ot', label: 'Overtime', color: 'var(--c-ot)' },
  { key: 'dt', label: 'Double time', color: 'var(--c-meal)', caOnly: true },
  { key: 'vac', label: 'Vacation', color: 'var(--c-svc)' },
  { key: 'sick', label: 'Sick', color: '#2fa3c2' },
  { key: 'hol', label: 'Holiday', color: 'var(--c-holiday)' },
  { key: 'unpaid', label: 'Unpaid time', color: '#8a93a2' },
];

function h(min: number | undefined): string {
  return min ? fmtHours(min) : '—';
}

/** The status right after a successful submit, until /portal/timesheet/status answers. */
function optimisticSubmitted(status: PortalTimesheetStatus | null, payPeriodID: number): PortalTimesheetStatus {
  return {
    payPeriodID,
    approvalStatus: null,
    submittedAtOffset: null,
    supervisorApprovedAt: null,
    payrollApprovedAt: null,
    exported: false,
    exportedAt: null,
    supervisorName: null,
    noSupervisor: false,
    ...(status ?? {}),
    submitted: true,
    locked: true,
    submittedAt: status?.submittedAt ?? new Date().toISOString(),
  };
}

function monDay(ymd: string): string {
  return fmtYmdShort(ymd);
}

const MyTimesheetPage: React.FC = () => {
  const {
    cfg, zone, self, loading: ctxLoading, loadError, periods: ctxPeriods, currentPeriod, previousPeriod, thisWeekStatus,
    lastWeekOpen, holidays: ctxHolidays, reload, reloadSelf, reportSessionExpired, timesheetIntent, clearTimesheetIntent,
  } = usePortalData();
  const { setPage } = useSiteContext();
  const toast = usePortalToast();
  const showToast = toast.show;

  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(t);
  }, []);
  const todayYmd = localYmd(nowMs, zone);

  /* ------------------------------------------------ which week */
  const [extraPeriods, setExtraPeriods] = useState<PortalPayPeriod[]>([]);
  const periods = useMemo(() => mergePeriods(ctxPeriods, extraPeriods), [ctxPeriods, extraPeriods]);
  const [viewID, setViewID] = useState<number | null>(null);
  const view: PortalPayPeriod | null = (viewID != null ? periods.find((p) => p.payPeriodID === viewID) : null) ?? currentPeriod ?? null;
  const [stepping, setStepping] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<number | null>(null);

  // Today's "Review & submit" (or this page's strip) asks for a week, optionally with the wizard.
  useEffect(() => {
    if (!timesheetIntent) return;
    setViewID(timesheetIntent.payPeriodID);
    setPendingSubmit(timesheetIntent.openSubmit ? timesheetIntent.payPeriodID : null);
    clearTimesheetIntent();
  }, [timesheetIntent, clearTimesheetIntent]);

  const goPrev = async () => {
    if (!view || stepping) return;
    const target = adjacentPeriod(periods, view, -1);
    if (target) {
      setViewID(target.payPeriodID);
      return;
    }
    // Older than the loaded window: load the weeks around the day before this one.
    setStepping(true);
    try {
      const day = addDaysYmd(view.startYmd, -1);
      const more = await getPortalPayPeriodsAround(day);
      setExtraPeriods((x) => mergePeriods(x, more));
      const p = periodContaining(more, day);
      if (p) setViewID(p.payPeriodID);
    } catch (err) {
      const info = describePortalError(err);
      if (info.kind === 'sessionExpired') reportSessionExpired();
      else showToast(info.message, 'warn');
    } finally {
      setStepping(false);
    }
  };
  const nextPeriod = view ? adjacentPeriod(periods, view, 1) : null;
  const isCurrentOrLater = !!view && view.endYmd >= todayYmd;
  const goNext = () => {
    if (nextPeriod && !isCurrentOrLater) setViewID(nextPeriod.payPeriodID);
  };

  /* ------------------------------------------------ week data */
  const [week, setWeek] = useState<WeekData | null>(null);
  const [weekLoading, setWeekLoading] = useState(false);
  const seq = useRef(0);

  const settle = useCallback(async <T,>(fn: () => Promise<T>): Promise<Settled<T>> => {
    try {
      return { ok: true, v: await fn() };
    } catch (err) {
      const info = describePortalError(err);
      if (info.kind === 'sessionExpired') reportSessionExpired();
      return { ok: false, info };
    }
  }, [reportSessionExpired]);

  const loadWeek = useCallback(async (p: PortalPayPeriod, employeeID: number) => {
    const mine = ++seq.current;
    setWeekLoading(true);
    const [ts, st, hol, wv, act] = await Promise.all([
      settle(() => getPortalTimesheet(employeeID, p.payPeriodID)),
      settle(() => getPortalTimesheetStatus(p.payPeriodID)),
      settle(() => getPortalHolidays(p.startYmd, p.endYmd)),
      settle(() => getPortalMealWaiverDates(p.payPeriodID)),
      settle(() => getPortalActivity({ payPeriodID: p.payPeriodID, weeks: 1 })),
    ]);
    let summary: PortalTimeSummary | null = null;
    let summaryError: string | null = null;
    if (ts.ok) {
      const ids = weekRowIDs(ts.v.timeEntries, ts.v.timeEntriesHourly);
      if (ids.timeEntryIDs.length || ids.timeEntryHourlyIDs.length) {
        const sm = await settle(() => getPortalTimeSummary({ employeeID, payPeriodID: p.payPeriodID, ...ids }));
        if (sm.ok) summary = sm.v;
        else summaryError = sm.info.message;
      }
    }
    if (mine !== seq.current) return;
    setWeek({
      payPeriodID: p.payPeriodID,
      timesheet: ts.ok ? ts.v : null,
      status: st.ok ? st.v : null,
      holidays: hol.ok ? hol.v : [],
      waiver: wv.ok ? wv.v : null,
      waiverFailed: !wv.ok,
      summary,
      summaryError,
      activity: act.ok ? itemsForWeek(act.v.items, p.startYmd) : null,
      error: !ts.ok ? ts.info : !st.ok ? st.info : null,
    });
    setWeekLoading(false);
  }, [settle]);

  const employeeID = self?.employeeID ?? null;
  const viewKey = view ? `${view.payPeriodID}` : '';
  useEffect(() => {
    if (view && employeeID != null) void loadWeek(view, employeeID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewKey, employeeID, loadWeek]);

  const reloadWeek = useCallback(async () => {
    if (view && employeeID != null) await loadWeek(view, employeeID);
  }, [view, employeeID, loadWeek]);

  /* ------------------------------------------------ questions (once) */
  const [questions, setQuestions] = useState<PortalTimesheetQuestion[] | null>(null);
  const [questionsFailed, setQuestionsFailed] = useState(false);
  const loadQuestions = useCallback(async () => {
    setQuestionsFailed(false);
    const r = await settle(() => getPortalTimesheetQuestions());
    if (r.ok) setQuestions(r.v);
    else setQuestionsFailed(true);
  }, [settle]);
  useEffect(() => { void loadQuestions(); }, [loadQuestions]);

  /* ------------------------------------------------ derived */
  const data = week && view && week.payPeriodID === view.payPeriodID ? week : null;
  const rows = useMemo(() => toTsRows(data?.timesheet?.timeEntriesHourly ?? []), [data]);
  const ymds = useMemo(() => (view ? weekYmds(view) : []), [view]);
  const totals = useMemo(() => totalsByDay(data?.summary ?? null), [data]);
  const weekTotals = sumTotals(totals, ymds);
  const holidayByYmd = useMemo(() => {
    const m = new Map<string, PortalHoliday>();
    for (const hd of data?.holidays ?? []) m.set((hd.holidayDate ?? '').slice(0, 10), hd);
    return m;
  }, [data]);
  const mealQDays = useMemo(() => new Set((data?.waiver?.perDay ?? []).filter((d) => d.hasWaiver).map((d) => d.workDate.slice(0, 10))), [data]);
  const status = data?.status ?? null;
  const rail = view ? railView(status, view, todayYmd) : null;
  const steps = railSteps(status);
  const lock = rail?.lock ?? null;
  const hasOpenPunch = rows.some((r) => r.outMs == null);
  const loadingWeek = !data || weekLoading;
  const block = submitBlockReason({ locked: !!lock, loading: loadingWeek, hasOpenPunch, loadFailed: !!data?.error });

  const workState = workStateOf(self?.role?.stateAbbr);
  const showDT = workState !== 'NV';
  const { jobCosting, nonJob } = useMemo(() => portalJobMode(cfg.jobCosting, self?.role), [cfg.jobCosting, self?.role]);
  // The non-job choice carries its cost code's DB name ("Non-Job Time") when any row has it.
  const overhead = useMemo(() => namedNonJob(nonJob, rows.map((r) => r.job)), [nonJob, rows]);
  const supervisorName = status?.supervisorName ?? thisWeekStatus?.supervisorName ?? null;
  const noSupervisor = status ? status.noSupervisor : !!thisWeekStatus?.noSupervisor;

  /* ------------------------------------------------ modals */
  const [dayYmd, setDayYmd] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  // A requested wizard opens once that week has loaded (and only if it is still open).
  useEffect(() => {
    if (pendingSubmit == null || !data || data.payPeriodID !== pendingSubmit || weekLoading) return;
    setPendingSubmit(null);
    if (!lock && !hasOpenPunch && !data.error) setWizardOpen(true);
  }, [pendingSubmit, data, weekLoading, lock, hasOpenPunch]);

  const describeWriteError = useCallback((err: unknown): string => {
    const info = describePortalError(err);
    if (info.kind === 'sessionExpired') reportSessionExpired();
    if (info.code === 'WEEK_SUBMITTED' || info.code === 'PAYROLL_EXPORTED') {
      void reload();
      void reloadWeek();
    }
    return info.message;
  }, [reportSessionExpired, reload, reloadWeek]);

  const onDayChanged = useCallback(async (message: string) => {
    await Promise.all([reloadWeek(), reloadSelf()]);
    if (message) showToast(message);
  }, [reloadWeek, reloadSelf, showToast]);

  const onSubmitted = useCallback(async () => {
    setWizardOpen(false);
    if (view) showToast(`Week of ${monDay(view.startYmd)} submitted to ${supervisorName ?? 'payroll'}`);
    // The POST succeeded, so the week IS submitted: show it at once (pill, rail, lock) instead
    // of "In progress" until the full week reload lands; the status refetch then confirms it.
    const submittedID = view?.payPeriodID ?? null;
    if (submittedID != null) {
      setWeek((w) => (w && w.payPeriodID === submittedID ? { ...w, status: optimisticSubmitted(w.status, submittedID) } : w));
      void getPortalTimesheetStatus(submittedID)
        .then((st) => setWeek((w) => (w && w.payPeriodID === submittedID ? { ...w, status: st } : w)))
        .catch(() => undefined);
    }
    await Promise.all([reloadWeek(), reload()]);
  }, [view, supervisorName, showToast, reloadWeek, reload]);

  /* ------------------------------------------------ per-day view model */
  const dayView = useMemo(() => ymds.map((ymd) => {
    const punches = dayPunches(rows, ymd, zone, todayYmd);
    const dayRs = rowsOnDay(rows, ymd, zone);
    const first = dayRs.length ? dayRs[0].inMs : null;
    const outs = dayRs.filter((r) => r.outMs != null).map((r) => r.outMs as number);
    const open = dayRs.some((r) => r.outMs == null);
    const range = first == null ? '' : `${shortTime(first, zone)}–${open ? (ymd === todayYmd ? 'now' : '?') : outs.length ? shortTime(Math.max(...outs), zone) : ''}`;
    return {
      ymd,
      punches,
      rows: dayRs,
      range,
      open,
      edited: punches.some((p) => p.edited),
      mealQ: mealQDays.has(ymd),
      worked: dayMinutes(rows, ymd, zone, nowMs).worked,
      jobs: dayJobs(rows, ymd, zone),
      t: totals.get(ymd) ?? ZERO_TOTALS,
      holiday: holidayByYmd.get(ymd) ?? null,
    };
  }), [ymds, rows, zone, todayYmd, mealQDays, nowMs, totals, holidayByYmd]);

  /* ------------------------------------------------ status strip (60px; same priorities as Today) */
  const holidayToday = ctxHolidays.find((x) => (x.holidayDate ?? '').slice(0, 10) === todayYmd) ?? null;
  const thisLock: 'submitted' | 'exported' | null = thisWeekStatus?.exported ? 'exported' : thisWeekStatus?.submitted ? 'submitted' : null;
  const stripKind = selectStrip({
    loading: ctxLoading && !self,
    pendingNetwork: false,
    loadError: !!loadError,
    otherDevice: false,
    weekLock: thisLock,
    noSupervisor: !!thisWeekStatus?.noSupervisor,
    lastWeekOpen,
    holidayToday: !!holidayToday,
  });
  const renderStrip = () => {
    const S = (tone: string, Icon: LucideIcon, text: React.ReactNode, action?: React.ReactNode) => (
      <div className={`strip ${tone}`} role="status"><Icon /><span className="st-tx">{text}</span>{action}</div>
    );
    const range = currentPeriod ? shortRange(currentPeriod.startYmd, currentPeriod.endYmd) : 'This week';
    switch (stripKind) {
      case 'loadError':
        return S('danger', WifiOff, <><b>Couldn't load your time.</b> {loadError?.message}</>,
          <button type="button" className="btn sm" onClick={() => void reload()}><RefreshCw />Retry</button>);
      case 'loading':
        return S('ok', Clock, <>Loading your time…</>);
      case 'locked':
        return thisLock === 'exported'
          ? S('warn', Lock, <><b>Payroll for this week ({range}) was exported</b>, so punches are locked. Contact payroll to make changes.</>)
          : S('warn', Lock, <><b>This week ({range}) was submitted</b>, so punches are locked. Ask your supervisor or payroll to unlock this week.</>);
      case 'noSupervisor': {
        const c = cfg.payrollContact;
        const contact = [c?.email, c?.phone].filter(Boolean).join(' or ');
        return S('danger', UserX, (
          <><b>No supervisor is assigned to you.</b> You can still submit — contact payroll{contact ? ` at ${contact}` : ''} so your timesheets reach an approver.</>
        ), c?.email ? <a className="btn sm" href={`mailto:${c.email}`}><Mail />Email payroll</a> : undefined);
      }
      case 'lastWeekOpen':
        return S('info', Send, (
          <><b>Last week ({previousPeriod ? shortRange(previousPeriod.startYmd, previousPeriod.endYmd) : ''}) isn't submitted.</b>
            {previousPeriod?.checkYmd ? <> Submit it so it makes the {fmtYmdDow(previousPeriod.checkYmd)} check.</> : <> Submit it when you're ready.</>}</>
        ), previousPeriod ? (
          <button type="button" className="btn sm primary" onClick={() => { setViewID(previousPeriod.payPeriodID); setPendingSubmit(previousPeriod.payPeriodID); }}>
            Review &amp; submit
          </button>
        ) : undefined);
      case 'holiday':
        return S('holiday', PartyPopper, <><b>Today is {holidayToday?.name}</b> — a company holiday.</>);
      default:
        return S('ok', CheckCircle2, <><b>You're all caught up.</b>{currentPeriod?.checkYmd ? <> This week pays {fmtYmdDow(currentPeriod.checkYmd)}.</> : null}</>);
    }
  };

  /* ------------------------------------------------ pieces */
  const fullName = self ? `${self.name.first} ${self.name.last}`.trim() : '';
  const rulesLabel = workState === 'NV' ? 'Nevada overtime rules' : workState === 'CA' ? 'California overtime rules' : 'Overtime rules';
  const headSub = (
    <>
      {[fullName, self?.altEmployeeID ? `#${self.altEmployeeID}` : null, 'Hourly', rulesLabel].filter(Boolean).join(' · ')}
      {' · Supervisor: '}
      {noSupervisor ? <b style={{ color: 'var(--c-meal)' }}>none assigned</b> : supervisorName ?? '—'}
    </>
  );

  const railNode = view && rail && (
    <div className="subrail">
      <div className="rail-st">
        <span className={`pill ${rail.tone}`}>{rail.pill}</span>
        <span className="rs-sub">{rail.sub}</span>
      </div>
      <div className="steps">
        {steps.map((s, k) => (
          <React.Fragment key={s.label}>
            {k > 0 && <span className="stepsep" />}
            <span className={`step ${s.state}`}>
              <span className="sn">{s.state === 'done' ? <Check /> : k === 3 ? <Lock /> : k + 1}</span>
              <span>
                <span className="sl">{s.label}</span>
                <span className="sd">{s.state === 'done' ? (s.at ? fmtDayShort(Date.parse(s.at), zone) : 'Done') : s.state === 'wait' ? 'Waiting' : '—'}</span>
              </span>
            </span>
          </React.Fragment>
        ))}
      </div>
      <div className="rail-act">
        {/* Locked: the strip / pill already say so — the slot stays (empty) so nothing moves. */}
        {lock ? null : (
          <>
            <button type="button" className="btn primary" disabled={!!block} title={block ?? undefined} onClick={() => setWizardOpen(true)}>
              <Send />Submit week
            </button>
            <span className={`rail-why${block && !loadingWeek ? ' warn' : ''}`}>
              {block && !loadingWeek ? block : noSupervisor ? 'No supervisor assigned — contact payroll' : ' '}
            </span>
          </>
        )}
      </div>
    </div>
  );

  const weekHoliday = dayView.find((d) => d.holiday)?.holiday ?? null;
  const weekNote = (() => {
    if (data?.error) {
      return (
        <div className="wknote err" role="alert">
          <WifiOff /><span className="wn-tx"><b>Couldn't load this week.</b> {data.error.message}</span>
          <button type="button" className="btn sm" onClick={() => void reloadWeek()}><RefreshCw />Retry</button>
        </div>
      );
    }
    if (weekHoliday) {
      const eligible = weekHoliday.eligibility ? weekHoliday.eligibility.isEligible : true;
      const day = fmtYmdDow(weekHoliday.holidayDate);
      return (
        <div className="wknote hol">
          <Sun />
          <span className="wn-tx">
            <b>{weekHoliday.name}</b> on {day} — {eligible
              ? <>{Number(weekHoliday.paidHoursIfNotWorked ?? 0).toFixed(2)} h holiday pay applied from the company calendar. Contact payroll if it looks wrong.</>
              : <>not paid for you{weekHoliday.eligibility?.reason ? `: ${weekHoliday.eligibility.reason}` : ' yet'}.</>}
          </span>
        </div>
      );
    }
    if (data?.summaryError) {
      return (
        <div className="wknote err">
          <Info /><span className="wn-tx"><b>Totals aren't available right now.</b> {data.summaryError}</span>
          <button type="button" className="btn sm" onClick={() => void reloadWeek()}><RefreshCw />Retry</button>
        </div>
      );
    }
    return (
      <div className="wknote">
        <Info />
        <span className="wn-tx">
          {workState === 'NV'
            ? <><b>Nevada overtime:</b> hours over 40 in the week (Mon–Sun) are OT. There's no daily overtime.</>
            : <><b>California overtime:</b> over 8 h a day is OT, over 12 h is double time, the 7th straight workday is premium, and regular hours over 40 a week are OT.</>}
          {hasOpenPunch ? ' A punch still open isn\'t counted until you clock out.' : ''}
        </span>
      </div>
    );
  })();

  const canOpenDay = (ymd: string, hasRows: boolean) => hasRows || (!lock && ymd <= todayYmd && !!data?.status && !!data.timesheet);

  const grid = (
    <div className="card ts-grid">
      <div className="wg-wrap">
        <table className="wg">
          {/* Fixed layout: the seven day columns are always equal, whatever they hold. */}
          <colgroup>
            <col className="wg-cat" />
            {dayView.map((d) => <col key={d.ymd} />)}
            <col className="wg-tot" />
          </colgroup>
          <thead>
            <tr>
              <th>Category</th>
              {dayView.map((d) => {
                const cls = [dowIndex(d.ymd) >= 5 ? 'wknd' : '', d.holiday ? 'hol' : '', d.ymd === todayYmd ? 'today' : ''].filter(Boolean).join(' ');
                return (
                  <th key={d.ymd} className={cls}>
                    {DOW_SHORT[dowIndex(d.ymd)]}<span className="dnum">{Number(d.ymd.slice(8, 10))}</span>
                  </th>
                );
              })}
              <th className="tcol">Total</th>
            </tr>
          </thead>
          <tbody>
            {CATEGORIES.filter((c) => showDT || !c.caOnly).map((c) => (
              <tr key={c.key} className="cat">
                <td><span className="catlab"><i style={{ background: c.color }} />{c.label}</span></td>
                {dayView.map((d) => {
                  const v = d.t[c.key];
                  const cls = [
                    'hrs', v ? '' : 'zero', dowIndex(d.ymd) >= 5 ? 'wknd' : '', d.ymd === todayYmd ? 'today' : '', d.holiday ? 'holc' : '',
                    (c.key === 'ot' || c.key === 'dt') && v ? 'otc' : '', c.key === 'reg' && d.edited ? 'edt' : '',
                  ].filter(Boolean).join(' ');
                  return <td key={d.ymd} className={cls} title={c.key === 'reg' && d.edited ? 'Has edited punches' : undefined}>{h(v)}</td>;
                })}
                <td className="hrs tcol">{h(weekTotals[c.key])}</td>
              </tr>
            ))}
            <tr className="tot">
              <td>Paid total</td>
              {dayView.map((d) => <td key={d.ymd} className="hrs">{h(d.t.total)}</td>)}
              <td className="hrs tcol2">{fmtHours(weekTotals.total)}</td>
            </tr>
            <tr className="meta">
              <td><span className="catlab sub"><Clock />Punches</span></td>
              {dayView.map((d) => (
                <td key={d.ymd} className={[dowIndex(d.ymd) >= 5 ? 'wknd' : '', d.ymd === todayYmd ? 'today' : ''].filter(Boolean).join(' ')}>
                  {d.rows.length ? (
                    <>
                      <button type="button" className="linkbtn pc" onClick={() => setDayYmd(d.ymd)}>{d.range}</button>
                      <span className="pc-tags">
                        {d.edited && <span className="edited">Edited</span>}
                        {d.mealQ && <span className="mflag" title="Meal question when you submit"><Coffee />Meal Q</span>}
                      </span>
                    </>
                  ) : d.holiday ? (
                    <span className="pill neutral">Holiday</span>
                  ) : canOpenDay(d.ymd, false) ? (
                    <button type="button" className="linkbtn" onClick={() => setDayYmd(d.ymd)}>Add</button>
                  ) : (
                    <span className="dim">—</span>
                  )}
                </td>
              ))}
              <td className="tcol" />
            </tr>
            {jobCosting && (
              <tr className="meta">
                <td><span className="catlab sub"><Briefcase />Working on</span></td>
                {dayView.map((d) => (
                  <td key={d.ymd} className={dowIndex(d.ymd) >= 5 ? 'wknd' : ''}>
                    {d.jobs.length === 0 ? <span className="dim">—</span>
                      : d.jobs.length === 1 ? <span className="tag mono">{jobCode(d.jobs[0])}</span>
                        : <button type="button" className="linkbtn" onClick={() => setDayYmd(d.ymd)}>{d.jobs.length} jobs</button>}
                  </td>
                ))}
                <td className="tcol" />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const chipKind = (kind: string) => (kind === 'in' ? 'in' : kind === 'out' ? 'out' : 'meal');
  const dayList = (
    <div className="card ts-day">
      <div className="dl">
        {dayView.map((d) => {
          const cls = [dowIndex(d.ymd) >= 5 ? 'wknd' : '', d.holiday ? 'hol' : '', d.ymd === todayYmd ? 'today' : ''].filter(Boolean).join(' ');
          const shown = d.punches.filter((p) => p.kind !== 'switch' && p.kind !== 'missingOut');
          return (
            <div key={d.ymd} className={`dl-row ${cls}`}>
              <span className="dl-d"><span className="dw">{DOW_LONG[dowIndex(d.ymd)]}</span><span className="dd">{monDay(d.ymd)}</span></span>
              <span className="dl-p">
                {d.holiday && <span className="chip c-holiday"><Sun />{d.holiday.name}</span>}
                {shown.map((p) => {
                  const Icon = p.kind === 'in' ? Play : p.kind === 'out' ? Square : Coffee;
                  return <span key={p.key} className={`punchchip ${chipKind(p.kind)}${p.edited ? ' edited' : ''}`}><Icon />{shortTime(p.atMs, zone)}</span>;
                })}
                {d.open && <span className="punchchip in"><Clock />{d.ymd === todayYmd ? 'now' : 'open'}</span>}
                {d.mealQ && <span className="mflag"><Coffee />Meal Q</span>}
                {d.t.sick > 0 && <span className="chip c-sick"><Stethoscope />Sick {fmtHours(d.t.sick)}</span>}
                {d.t.vac > 0 && <span className="chip c-svc"><Sun />Vacation {fmtHours(d.t.vac)}</span>}
                {d.t.unpaid > 0 && <span className="chip c-unpaid">Unpaid {fmtHours(d.t.unpaid)}</span>}
                {!d.rows.length && !d.holiday && !d.t.sick && !d.t.vac && <span className="dim">{d.ymd > todayYmd ? 'Upcoming' : 'No punches'}</span>}
              </span>
              <span className="dl-h">{h(d.t.total)}{d.t.ot + d.t.dt ? <small>+{fmtHours(d.t.ot + d.t.dt)} OT</small> : null}</span>
              <span className="dl-a">
                {canOpenDay(d.ymd, d.rows.length > 0) && (
                  <button type="button" className="iconbtn" aria-label={`Open ${DOW_LONG[dowIndex(d.ymd)]}`} onClick={() => setDayYmd(d.ymd)}>
                    <ChevronRight />
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const totalsStrip = (
    <div className="pp">
      <div className="pp-c"><div className="pl">Regular</div><div className="pv">{fmtHours(weekTotals.reg)}</div><div className="ps">Regular hours this week</div></div>
      <div className="pp-c ot">
        <div className="pl">Overtime</div><div className="pv">{fmtHours(weekTotals.ot + weekTotals.dt)}</div>
        <div className="ps">{showDT ? `${fmtHours(weekTotals.ot)} OT · ${fmtHours(weekTotals.dt)} DT` : `${fmtHours(weekTotals.ot)} OT`}</div>
      </div>
      <div className="pp-c pto">
        <div className="pl">Paid leave</div><div className="pv">{fmtHours(weekTotals.vac + weekTotals.sick + weekTotals.hol)}</div>
        <div className="ps">
          {fmtHours(weekTotals.vac)} vac · {fmtHours(weekTotals.sick)} sick · {fmtHours(weekTotals.hol)} hol{weekTotals.unpaid ? ` · ${fmtHours(weekTotals.unpaid)} unpaid` : ''}
        </div>
      </div>
      <div className="pp-c tot">
        <div className="pl">Week total</div><div className="pv">{fmtHours(weekTotals.total)}</div>
        <div className="ps">{hasOpenPunch ? 'Open punch not counted yet' : view?.checkYmd ? `Pays ${fmtYmdDow(view.checkYmd)}` : ' '}</div>
      </div>
    </div>
  );

  const card = (Icon: LucideIcon, title: string, sub: string | null, body: React.ReactNode, right?: React.ReactNode, cls = 'fixc') => (
    <div className={`card ${cls}`}>
      <div className="card-head">
        <span className="ch-ic"><Icon /></span>
        <div className="ch-tx"><h3>{title}</h3>{sub ? <div className="ch-sub">{sub}</div> : null}</div>
        {right ? <div className="right">{right}</div> : null}
      </div>
      {body}
    </div>
  );

  const otDays = dayView.filter((d) => d.t.ot + d.t.dt > 0);
  const otCard = card(BarChart3, 'Overtime this week', workState === 'NV' ? 'Nevada rules' : 'California rules · daily, 7th day and weekly', (
    <div className="cbody">
      {otDays.length === 0 && <div className="pk-empty">No overtime this week</div>}
      {otDays.map((d) => (
        <div key={d.ymd} className="ot-row">
          <span className="od">{fmtYmdDow(d.ymd)}</span>
          <span className="ow">{fmtHours(d.worked)} h worked</span>
          <span className="oy">{fmtHours(d.t.ot)} OT{showDT ? ` · ${fmtHours(d.t.dt)} DT` : ''}</span>
          <span className="ov">+{fmtHours(d.t.ot + d.t.dt)}</span>
        </div>
      ))}
      <div className="ot-foot">Paid leave and holiday hours don't count toward the 40.</div>
    </div>
  ), undefined, 'fitc');

  const waiverDates = data?.waiver?.waiverDates ?? [];
  const previewQs = questions ? visibleQuestions(questions, waiverDates) : [];
  // After submit: the answers as stored (null with an older API that doesn't send them).
  const answered = questions && status?.submitted ? submittedAnswers(questions, status.answers) : null;
  const submittedAt = status?.submittedAt ? Date.parse(status.submittedAt) : null;
  const qSub = status?.submitted
    ? `Signed electronically${submittedAt ? ` · ${fmtDayShort(submittedAt, zone)}` : ''}`
    : "You'll answer these when you submit";
  const questionsCard = card(ClipboardCheck, 'Timesheet questions', qSub, (
    <div className="cbody pad">
      {questionsFailed && <div className="pk-empty">Couldn't load the questions.</div>}
      {!questions && !questionsFailed && <div className="pk-empty">Loading…</div>}
      {questions && (answered ? answered.length === 0 : previewQs.length === 0) && <div className="pk-empty">No questions this week</div>}
      <div className="ansum">
        {answered?.map((a, k) => (
          <div key={a.id} className={`ar${a.child ? ' child' : ''}`}>
            <span className="aq"><b>Q{k + 1}</b> · {questionText(a.question, waiverDates)}</span>
            <span className="aa">{a.answer}</span>
          </div>
        ))}
        {!answered && previewQs.map((q, k) => (
          <div key={q.timesheetQuestionID} className="ar">
            <span className="aq"><b>Q{k + 1}</b> · {questionText(q.question, waiverDates)}</span>
            <span className="aa dim">
              {status?.submitted ? 'On file' : q.answerType === ANSWER_MEAL_WAIVER ? waiverDates.join(', ') : '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  ));

  const changes = data?.activity ?? null;
  const changesCard = card(History, 'Changes to this week',
    // Always a sub-line (a no-break space when there's nothing to count) so the header height never changes.
    changes && changes.length ? (changes.length === 1 ? '1 change' : `${changes.length} changes`) : ' ', (
      <div className="cbody act-mini">
        {changes?.map((item) => <ActivityRow key={item.id} item={item} zone={zone} todayYmd={todayYmd} mini />)}
        {(!changes || changes.length === 0) && (
          <div className="pk-empty">
            {loadingWeek ? 'Loading…' : data && !changes ? (
              <>Couldn't load this week's changes<button type="button" className="linkbtn" onClick={() => void reloadWeek()}>Try again</button></>
            ) : 'No changes recorded this week'}
          </div>
        )}
      </div>
    ), <button type="button" className="linkbtn" onClick={() => setPage(Page.MyActivity)}>Full activity</button>);

  /* ------------------------------------------------ page */
  const wizardDays: WizardDay[] = dayView.map((d) => ({ ymd: d.ymd, punches: d.range, workedMin: d.worked, mealQ: d.mealQ }));
  const ids = data?.timesheet ? weekRowIDs(data.timesheet.timeEntries, data.timesheet.timeEntriesHourly) : { timeEntryIDs: [], timeEntryHourlyIDs: [] };
  const isThisWeek = !!view && !!currentPeriod && view.payPeriodID === currentPeriod.payPeriodID;

  return (
    <div className="etp">
      <div className="page-head">
        <div className="ph-ic"><ClipboardCheck /></div>
        <div className="ph-tx">
          <h1>My Timesheet</h1>
          <div className="ph-sub">{self ? headSub : 'Your week, punch by punch'}</div>
        </div>
        <div className="ph-actions">
          <div className="wk-nav">
            <button type="button" aria-label="Previous week" disabled={!view || stepping} onClick={() => void goPrev()}><ChevronLeft /></button>
            <span className="wk-lab">
              {view ? weekLabel(view) : '—'}
              <small>{view?.checkYmd ? `Pays ${fmtYmdDow(view.checkYmd)}` : ' '}</small>
            </span>
            <button type="button" aria-label="Next week" disabled={!nextPeriod || isCurrentOrLater} onClick={goNext}><ChevronRight /></button>
          </div>
          <button type="button" className="btn wk-today" disabled={!currentPeriod || isThisWeek} onClick={() => currentPeriod && setViewID(currentPeriod.payPeriodID)}>
            This week
          </button>
        </div>
      </div>

      {renderStrip()}

      {view && self ? (
        <>
          {railNode}
          {weekNote}
          {grid}
          {dayList}
          {totalsStrip}
          <div className="grid2e ts-cards" style={{ marginTop: 16 }}>
            {otCard}
            {questionsCard}
          </div>
          <div style={{ marginTop: 16 }}>{changesCard}</div>
        </>
      ) : (
        <div className="card"><div className="soon"><div className="eic"><ClipboardCheck /></div><div className="et">{loadError ? "Your timesheet isn't available" : 'Loading your week…'}</div></div></div>
      )}

      {dayYmd && view && self && (
        <DayDetailModal
          ymd={dayYmd}
          rows={rows}
          period={view}
          periods={periods}
          zone={zone}
          todayYmd={todayYmd}
          nowMs={nowMs}
          totals={totals.get(dayYmd) ?? null}
          lock={lock}
          statusUnknown={!data?.status}
          jobCosting={jobCosting}
          defaultJob={overhead}
          employeeID={self.employeeID}
          onClose={() => setDayYmd(null)}
          onChanged={onDayChanged}
          describeError={describeWriteError}
          onSessionExpired={() => reportSessionExpired()}
        />
      )}

      {wizardOpen && view && self && (
        <SubmitWizard
          period={view}
          days={wizardDays}
          totals={totals}
          waiver={data?.waiver ?? null}
          waiverFailed={!!data?.waiverFailed}
          questions={questions}
          questionsFailed={questionsFailed}
          onRetryLoads={() => { void loadQuestions(); void reloadWeek(); }}
          supervisorName={supervisorName}
          employeeID={self.employeeID}
          name={{ first: self.name.first, middle: self.name.middle ?? null, last: self.name.last }}
          zone={zone}
          timeEntryIDs={ids.timeEntryIDs}
          timeEntryHourlyIDs={ids.timeEntryHourlyIDs}
          onClose={() => setWizardOpen(false)}
          onSubmitted={onSubmitted}
          onSessionExpired={() => reportSessionExpired()}
          roleBlock={portalSubmitBlockedByRole(self.role)}
        />
      )}

      {toast.node}
    </div>
  );
};

export default MyTimesheetPage;
